"use client";

import type { AddonDietAddonsRead } from "@repo/api/catalog/api/schemas/addon-diet-addons-read";
import type { DietAddonGroupDietAddonsRead } from "@repo/api/catalog/api/schemas/diet-addon-group-diet-addons-read";
import { ResponsiveDialog } from "@repo/ui/components/responsive-dialog";
import { useIsMobile } from "@repo/ui/lib/hooks/use-mobile";
import { IconArrowLeft } from "@tabler/icons-react";
import { cs, enUS, pl } from "date-fns/locale";
import { useLocale, useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { useAddonsConfigStore } from "@/features/addons/components/addons-config-provider";
import { useDietAddonModal } from "@/features/addons/hooks/use-diet-addon-modal";
import { findAddonCategoryName } from "@/features/addons/lib/addon-gtm-item";
import { summarizeSelection } from "@/features/addons/lib/aggregate-selected-addons";
import type { SubItemsPricing } from "@/features/addons/types/sub-Items-pricing.type";
import { useAddonRemoveFromCartEvent } from "@/features/analytics/hooks/use-addon-remove-from-cart-event";
import { useAddonViewItemEvent } from "@/features/analytics/hooks/use-addon-view-item-event";
import { getLocalePrice } from "@/lib/utilities";
import { DietAddonEditorBody } from "./diet-addon-editor-body";
import { DietAddonsSummaryContent } from "./diet-addons-summary-content";

const dateFnsLocales = { pl, en: enUS, cz: cs };

const SUBMIT_BUTTON_CLASS =
  "h-auto rounded-xl bg-brand-yellow px-8 py-4 font-medium text-base text-brand-green2-300 hover:bg-brand-yellow-hover disabled:opacity-40";

// Whether the editor is adding or editing is derived from the committed
// entries, not from the button that opened it: adding an addon that is already
// in the selection is an edit (MPL-3844).
type EditorTarget = { addon: AddonDietAddonsRead };

type DietAddonsSummaryDialogProps = {
  categories: DietAddonGroupDietAddonsRead[];
  // Diet delivery days chosen in the calendar; addons attach only to these days.
  dietDates: Date[];
  // Quote of the addons already committed to the position, so their rows show
  // the charged amount. Entries added in this dialog are not quoted yet and
  // fall back to the catalog estimate until the change is persisted.
  subItemsPricing?: SubItemsPricing | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Brand reported for addons — they carry no brand of their own, so the parent
  // diet's catering is used.
  cateringName?: string;
};

// Summary dialog (MPL-3728): lists the selected addons and lets the user browse
// available ones. The add/edit view replaces this dialog's content in place
// (slide animation + back arrow) instead of opening a separate window.
export const DietAddonsSummaryDialog = ({
  categories,
  dietDates,
  subItemsPricing,
  open,
  onOpenChange,
  cateringName,
}: DietAddonsSummaryDialogProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const isMobile = useIsMobile();
  const calendarLocale =
    dateFnsLocales[locale as keyof typeof dateFnsLocales] ?? pl;

  const [editorTarget, setEditorTarget] = useState<EditorTarget | null>(null);

  const entries = useAddonsConfigStore((store) => store.addons);
  const getAddonEntries = useAddonsConfigStore(
    (store) => store.getAddonEntries,
  );
  const setAddonEntries = useAddonsConfigStore(
    (store) => store.setAddonEntries,
  );
  const seedAddons = useAddonsConfigStore((store) => store.seedAddons);

  const summary = useMemo(
    () => summarizeSelection(categories, entries, subItemsPricing),
    [categories, entries, subItemsPricing],
  );

  const activeAddon = editorTarget?.addon ?? null;
  // Read the committed entries for the addon being edited so the editor starts
  // pre-filled; empty for a fresh add.
  const initialEntries = activeAddon ? getAddonEntries(activeAddon.id) : [];

  const editor = useDietAddonModal({
    addon: activeAddon,
    dietDates,
    initialEntries,
  });

  const activeCategoryName = useMemo(
    () =>
      activeAddon
        ? findAddonCategoryName(categories, activeAddon.id)
        : undefined,
    [activeAddon, categories],
  );

  const reportRemovedAddon = useAddonRemoveFromCartEvent();

  // The in-place editor is the same "pick the days" view as the standalone
  // modal, so it reports view_item too — but only while it is the visible pane.
  useAddonViewItemEvent({
    addon: activeAddon,
    cateringName,
    categoryName: activeCategoryName,
    enabled: open && Boolean(editorTarget),
    selectedDates: editor.selectedDates,
    totalQuantity: editor.totalQuantity,
  });

  const findAddon = (addonId: string): AddonDietAddonsRead | undefined => {
    for (const category of categories) {
      const found = category.addons.find((addon) => addon.id === addonId);
      if (found) {
        return found;
      }
    }
    return undefined;
  };

  const openAdd = (addon: AddonDietAddonsRead) => setEditorTarget({ addon });
  const openEdit = (addonId: string) => {
    const addon = findAddon(addonId);
    if (addon) {
      setEditorTarget({ addon });
    }
  };

  const backToSummary = () => setEditorTarget(null);

  const commitEditor = () => {
    if (!activeAddon) {
      return;
    }
    // Only adding needs at least one day. The button stays enabled at zero so
    // the user gets an explicit reason instead of a dead button.
    if (!(editor.isEditing || editor.totalQuantity > 0)) {
      toast.error(t("cart.configuration.addons.modal.noDaysSelected"));
      return;
    }
    if (!editor.canCommit) {
      return;
    }
    if (editor.isRemoval) {
      // Confirming an empty edit drops the addon from the selection, so report
      // it here — leaving the editor changes nothing and is not reported.
      reportRemovedAddon({
        addon: activeAddon,
        cateringName,
        categoryName: activeCategoryName,
        entries: initialEntries,
        pricing: subItemsPricing,
      });
    }
    setAddonEntries(activeAddon.id, editor.buildEntries());
    setEditorTarget(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setEditorTarget(null);
    }
    onOpenChange(next);
  };

  const header = editorTarget ? (
    <div className="flex items-center gap-3">
      <button
        aria-label={t("cart.configuration.addons.summary.back")}
        className="flex size-8 shrink-0 items-center justify-center rounded-lg text-brand-green2-300 hover:bg-brand-grey1"
        onClick={backToSummary}
        type="button"
      >
        <IconArrowLeft className="size-5" stroke={2} />
      </button>
      <span className="font-medium text-2xl text-brand-green2-300">
        {editor.isEditing
          ? t("cart.configuration.addons.summary.editTitle")
          : t("cart.configuration.addons.summary.addTitle")}
      </span>
    </div>
  ) : (
    <span className="font-medium text-2xl text-brand-green2-300">
      {t("cart.configuration.addons.summary.title")}
    </span>
  );

  const footer = editorTarget ? (
    <div className="flex w-full items-center justify-between gap-4">
      <div className="flex min-w-0 flex-col">
        <span className="font-bold text-brand-violet text-xl leading-7">
          {getLocalePrice({ price: editor.totalPrice })}
        </span>
        <span className="text-brand-green2-300 text-sm leading-5.5">
          {t("cart.configuration.addons.modal.summary", {
            count: editor.totalQuantity,
            price: getLocalePrice({ price: editor.perUnitPrice }),
          })}
        </span>
      </div>
      <Button
        className={SUBMIT_BUTTON_CLASS}
        onClick={commitEditor}
        type="button"
      >
        {editor.isEditing
          ? t("cart.configuration.addons.modal.confirm")
          : t("cart.configuration.addons.modal.submit")}
      </Button>
    </div>
  ) : (
    <div className="flex w-full items-center justify-between gap-4">
      <div className="flex min-w-0 flex-col">
        <span className="font-bold text-brand-violet text-xl leading-7">
          {getLocalePrice({ price: summary.productTotal })}
        </span>
        <span className="text-brand-green2-300 text-sm leading-5.5">
          {t("cart.configuration.addons.summary.footerSummary", {
            count: summary.totalQuantity,
            days: summary.daysCount,
          })}
        </span>
      </div>
      <Button
        className={SUBMIT_BUTTON_CLASS}
        onClick={() => handleOpenChange(false)}
        type="button"
      >
        {t("cart.configuration.addons.summary.confirm")}
      </Button>
    </div>
  );

  const body = editorTarget ? (
    <div
      className="slide-in-from-right-8 animate-in duration-300 md:min-h-0 md:flex-1 md:overflow-y-auto"
      key="editor"
    >
      <div className="p-4 md:p-6">
        <DietAddonEditorBody
          addon={editorTarget.addon}
          allowedDates={editor.allowedDates}
          cateringName={cateringName}
          allowedKeys={editor.allowedKeys}
          calendarLocale={calendarLocale}
          canAddMore={editor.canAddMore}
          isAllSelected={editor.isAllSelected}
          onDecrementDay={editor.decrementDay}
          onIncrementDay={editor.incrementDay}
          onRemoveDay={editor.removeDay}
          onSyncSelectedDates={editor.syncSelectedDates}
          onToggleAll={editor.toggleAll}
          selectedDates={editor.selectedDates}
          selection={editor.selection}
        />
      </div>
    </div>
  ) : (
    <div
      className="slide-in-from-left-8 animate-in duration-300 md:flex md:min-h-0 md:flex-1"
      key="summary"
    >
      <DietAddonsSummaryContent
        cateringName={cateringName}
        categories={categories}
        daysCount={dietDates.length}
        isMobile={isMobile}
        isVisible={open && !editorTarget}
        items={summary.items}
        onAddAddon={openAdd}
        onEditAddon={openEdit}
        onRemoveAddon={(addonId) => setAddonEntries(addonId, [])}
        onRemoveAll={() => seedAddons([])}
      />
    </div>
  );

  return (
    <ResponsiveDialog
      contentClassName={
        isMobile
          ? "bg-brand-grey1"
          : "flex min-h-0 flex-1 flex-col overflow-hidden bg-brand-grey1"
      }
      dialogClassName="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0  md:w-[calc(100%-2rem)] md:max-w-[1080px] max-h-[775px] md:h-[775px]"
      footer={footer}
      footerClassName="border-t bg-brand-white px-4 py-4 md:px-6"
      fullHeight
      header={header}
      headerClassName="border-b bg-brand-white px-4 py-4 md:px-6"
      onOpenChange={handleOpenChange}
      open={open}
    >
      {body}
    </ResponsiveDialog>
  );
};
